import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SearchBar as SearchBarInput } from '@fikasio/react-ui-components';
import SearchResultTypeIcon from 'components/UI/SearchResultTypeIcon';
import { useGetObjectivesQuery } from 'services/objectives/api';
import { FEATURES } from 'services/features/catalog';
import { useFeatureEnabled } from 'services/features/hooks';
import { useGetProjectsQuery } from 'services/projects/api';
import { useGetTasksQuery } from 'services/tasks/api';
import links from 'utils/links';
import { getSearchResultPath, getSearchResults, SearchResult } from 'utils/search';

const SearchBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { enabled: objectivesEnabled } = useFeatureEnabled(FEATURES.objectives);
  const { enabled: tasksEnabled } = useFeatureEnabled(FEATURES.tasks);
  const { data: objectives } = useGetObjectivesQuery(undefined, { skip: !objectivesEnabled });
  const { data: projects } = useGetProjectsQuery();
  const { data } = useGetTasksQuery({}, { skip: !tasksEnabled });
  const visibleObjectives = objectivesEnabled ? objectives : undefined;
  const tasks = tasksEnabled ? data?.tasks : undefined;

  const results = useMemo(
    () => getSearchResults(query, { objectives: visibleObjectives, projects, tasks }),
    [projects, query, tasks, visibleObjectives],
  );
  const suggestions = useMemo(() => results.slice(0, 10), [results]);

  const goToSuggestion = (suggestion?: SearchResult) => {
    if (!suggestion) {
      return;
    }
    navigate(getSearchResultPath(suggestion));
  };

  const handleSubmit = () => {
    if (results.length === 1) {
      goToSuggestion(results[0]);
      return;
    }
    if (results.length > 1) {
      navigate(links.search(query.trim()));
    }
  };

  return (
    <SearchBarInput
      ariaLabel={t('search')}
      autoComplete="off"
      filterOptions={false}
      getOptionKey={suggestion => `${suggestion.type}-${suggestion.id}`}
      onChange={setQuery}
      onSelect={goToSuggestion}
      onSubmit={handleSubmit}
      options={suggestions}
      renderOption={suggestion => (
        <>
          <SearchResultTypeIcon
            className="fikasio-searchbar-option-icon"
            type={suggestion.type}
          />
          <span className="fikasio-searchbar-option-label">{suggestion.name}</span>
        </>
      )}
      style={{ width: 320 }}
      type="search"
      value={query}
    />
  );
};

export default SearchBar;
